import { useTranslation } from 'react-i18next'
import { Check, FileText } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useAuthModal } from '@/features/auth'

function PricingCard() {
  const { t } = useTranslation()
  const { openAuthModal } = useAuthModal()

  const features = [
    t('landing.pricing.feature1'),
    t('landing.pricing.feature2'),
    t('landing.pricing.feature3'),
    t('landing.pricing.feature4'),
    t('landing.pricing.feature5'),
  ]

  return (
    <div
      className={cn(
        'flex flex-col rounded-2xl border-2 border-accent bg-white p-8',
        'transition-transform duration-200 ease-out',
        'hover:scale-[1.02]'
      )}
    >
      {/* Badge */}
      <div className="mb-6">
        <span className="inline-block rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-white">
          {t('landing.pricing.badge')}
        </span>
      </div>

      {/* Plan Title */}
      <h3 className="text-xl font-bold text-foreground">
        {t('landing.pricing.plan.title')}
      </h3>

      {/* Price */}
      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-2xl font-medium text-foreground-muted line-through">
          {t('landing.pricing.plan.priceOriginal')}
        </span>
        <span className="text-5xl font-bold text-foreground">
          {t('landing.pricing.plan.price')}
        </span>
        <span className="text-foreground-muted">
          {t('landing.pricing.plan.period')}
        </span>
      </div>

      {/* Features */}
      <ul className="mt-8 flex-1 space-y-4">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-success">
              <Check className="h-3 w-3 text-white" />
            </div>
            <span className="text-foreground">{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <button
        onClick={() => openAuthModal()}
        className={cn(
          'mt-8 w-full rounded-lg bg-accent px-6 py-4 text-lg font-semibold text-white',
          'transition-all duration-200 ease-out',
          'hover:-translate-y-0.5 hover:bg-accent-hover hover:shadow-lg hover:shadow-accent/30',
          'focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2'
        )}
      >
        {t('landing.pricing.cta')}
      </button>
    </div>
  )
}

function PDFPreview() {
  const { t } = useTranslation()

  const sections = t('landing.pricing.preview.sections', { returnObjects: true }) as string[]

  return (
    <div
      className={cn(
        'flex flex-col rounded-2xl bg-white p-6',
        'shadow-[0_8px_32px_-8px_rgba(0,0,0,0.12)]',
        'transition-all duration-300 ease-out',
        'hover:-translate-y-1 hover:rotate-1 hover:shadow-[0_12px_40px_-8px_rgba(0,0,0,0.18)]'
      )}
    >
      {/* Label */}
      <span className="mb-4 text-sm font-medium text-foreground-muted">
        {t('landing.pricing.preview.label')}
      </span>

      {/* Document Header */}
      <div className="mb-6 flex items-center gap-3 border-b border-border pb-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent">
          <FileText className="h-5 w-5 text-white" />
        </div>
        <span className="text-lg font-bold text-foreground">
          {t('landing.pricing.preview.title')}
        </span>
      </div>

      {/* Sections List */}
      <ul className="flex-1 space-y-3">
        {sections.map((section, index) => (
          <li key={index} className="flex items-center gap-3">
            <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-success">
              <Check className="h-3 w-3 text-white" />
            </div>
            <span className="text-sm text-foreground">{section}</span>
          </li>
        ))}
      </ul>

      {/* Footer */}
      <div className="mt-6 border-t border-border pt-4">
        <span className="text-sm text-foreground-muted">
          {t('landing.pricing.preview.pages')}
        </span>
      </div>
    </div>
  )
}

/**
 * Pricing section with card and PDF preview
 * Implementation: LAND2-004
 */
export function PricingSection() {
  const { t } = useTranslation()

  return (
    <div className="bg-background-secondary py-16 sm:py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 text-center lg:mb-16">
          <h2 className="text-3xl font-bold text-foreground sm:text-4xl lg:text-5xl">
            {t('landing.pricing.title')}
          </h2>
          <p className="mt-4 text-lg text-foreground-muted sm:text-xl">
            {t('landing.pricing.subtitle')}
          </p>
        </div>

        {/* Content Grid */}
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
          {/* Pricing Card */}
          <PricingCard />

          {/* PDF Preview */}
          <PDFPreview />
        </div>
      </div>
    </div>
  )
}
